/**
 * Creates and returns a confetti animation controller
 * @returns {Object} Confetti controller with various methods
 */
export default function confetti() {
	// Configure based on screen size
	const isMobile = window.screen.width < 991;
	const maxCount = isMobile ? 50 : 150;
	
	// Confetti configuration object
	const confettiConfig = {
		maxCount: maxCount,
		speed: 2,
		frameInterval: 15,
		alpha: 1,
		gradient: false,
		start: null,
		stop: null,
		toggle: null,
		pause: null,
		resume: null,
		togglePause: null,
		remove: null,
		isPaused: null,
		isRunning: null
	};

	// Animation variables
	let isRunning = false;
	let isPaused = false;
	let lastFrameTime = Date.now();
	let particles = [];
	let waveAngle = 0;
	let context = null;
	
	// Animation frame handling
	const requestAnimFrame = 
		window.requestAnimationFrame || 
		window.webkitRequestAnimationFrame || 
		window.mozRequestAnimationFrame || 
		window.oRequestAnimationFrame || 
		window.msRequestAnimationFrame || 
		function(callback) {
			return window.setTimeout(callback, confettiConfig.frameInterval);
		};
	
	// Color palette
	const colors = [
		"rgba(30,144,255,", "rgba(107,142,35,", "rgba(255,215,0,", 
		"rgba(255,192,203,", "rgba(106,90,205,", "rgba(173,216,230,", 
		"rgba(238,130,238,", "rgba(152,251,152,", "rgba(70,130,180,", 
		"rgba(244,164,96,", "rgba(210,105,30,", "rgba(220,20,60,"
	];
	
	/**
	 * Creates a new particle
	 */
	function createParticle(options, width, height) {
		options.color = colors[Math.random() * colors.length | 0] + (confettiConfig.alpha + ")");
		options.color2 = colors[Math.random() * colors.length | 0] + (confettiConfig.alpha + ")");
		options.x = Math.random() * width;
		options.y = Math.random() * height - height;
		options.diameter = 10 * Math.random() + 5;
		options.tilt = 10 * Math.random() - 10;
		options.tiltAngleIncrement = 0.07 * Math.random() + 0.05;
		options.tiltAngle = Math.random() * Math.PI;
		return options;
	}
	
	/**
	 * Pauses the animation
	 */
	function pauseConfetti() {
		isPaused = true;
	}
	
	/**
	 * Resumes the animation
	 */
	function resumeConfetti() {
		isPaused = false;
		runAnimation();
	}
	
	/**
	 * Runs the animation loop
	 */
	function runAnimation() {
		if (isPaused) return;
		
		if (particles.length === 0) {
			context.clearRect(0, 0, window.innerWidth, window.innerHeight);
			return;
		}
		
		const currentTime = Date.now();
		const delta = currentTime - lastFrameTime;
		
		if (delta > confettiConfig.frameInterval) {
			context.clearRect(0, 0, window.innerWidth, window.innerHeight);
			
			updateParticles();
			drawParticles(context);
			
			lastFrameTime = currentTime - (delta % confettiConfig.frameInterval);
		}
		
		requestAnimFrame(runAnimation);
	}
	
	/**
	 * Updates particle positions
	 */
	function updateParticles() {
		const width = window.innerWidth;
		const height = window.innerHeight;
		waveAngle += 0.01;
		
		for (let i = 0; i < particles.length; i++) {
			const particle = particles[i];
			
			if (!isRunning && particle.y < -15) {
				particle.y = height + 100;
			} else {
				particle.tiltAngle += particle.tiltAngleIncrement;
				particle.x += Math.sin(waveAngle) - 0.5;
				particle.y += 0.5 * (Math.cos(waveAngle) + particle.diameter + confettiConfig.speed);
				particle.tilt = 15 * Math.sin(particle.tiltAngle);
			}
			
			if (particle.x > width + 20 || particle.x < -20 || particle.y > height) {
				if (isRunning && particles.length <= confettiConfig.maxCount) {
					createParticle(particle, width, height);
				} else {
					particles.splice(i, 1);
					i--;
				}
			}
		}
	}
	
	/**
	 * Draws all particles
	 */
	function drawParticles(context) {
		for (let i = 0; i < particles.length; i++) {
			const particle = particles[i];
			context.beginPath();
			context.lineWidth = particle.diameter;
			
			const x2 = particle.x + particle.tilt;
			const x1 = x2 + particle.diameter / 2;
			const y2 = particle.y + particle.tilt + particle.diameter / 2;
			
			if (confettiConfig.gradient) {
				const gradient = context.createLinearGradient(x1, particle.y, x2, y2);
				gradient.addColorStop("0", particle.color);
				gradient.addColorStop("1.0", particle.color2);
				context.strokeStyle = gradient;
			} else {
				context.strokeStyle = particle.color;
			}
			
			context.moveTo(x1, particle.y);
			context.lineTo(x2, y2);
			context.stroke();
		}
	}
	
	/**
	 * Starts the confetti animation
	 */
	function startConfetti(timeout, min, max) {
		const width = window.innerWidth;
		const height = window.innerHeight;
		
		// Set up canvas if needed
		let canvas = document.getElementById("confetti-canvas");
		if (canvas === null) {
			canvas = document.createElement("canvas");
			canvas.setAttribute("id", "confetti-canvas");
			canvas.setAttribute("style", "display:block;z-index:999999;pointer-events:none;position:fixed;top:0");
			document.body.prepend(canvas);
			canvas.width = width;
			canvas.height = height;
			window.addEventListener("resize", function() {
				canvas.width = window.innerWidth;
				canvas.height = window.innerHeight;
			}, true);
			context = canvas.getContext("2d");
		} else if (context === null) {
			context = canvas.getContext("2d");
		}
		
		// Calculate the number of particles to add
		let count = confettiConfig.maxCount;
		
		if (min) {
			if (max) {
				if (min === max) {
					count = particles.length + max;
				} else {
					if (min > max) {
						const temp = min;
						min = max;
						max = temp;
					}
					count = particles.length + ((Math.random() * (max - min) + min) | 0);
				}
			} else {
				count = particles.length + min;
			}
		} else if (max) {
			count = particles.length + max;
		}
		
		// Add particles
		while (particles.length < count) {
			particles.push(createParticle({}, width, height));
		}
		
		isRunning = true;
		isPaused = false;
		runAnimation();
		
		if (timeout) {
			window.setTimeout(stopConfetti, timeout);
		}
	}
	
	/**
	 * Stops the confetti animation
	 */
	function stopConfetti() {
		isRunning = false;
	}
	
	// Assign methods to the confetti object
	confettiConfig.start = startConfetti;
	confettiConfig.stop = stopConfetti;
	confettiConfig.toggle = function() {
		if (isRunning) {
			stopConfetti();
		} else {
			startConfetti();
		}
	};
	confettiConfig.pause = pauseConfetti;
	confettiConfig.resume = resumeConfetti;
	confettiConfig.togglePause = function() {
		if (isPaused) {
			resumeConfetti();
		} else {
			pauseConfetti();
		}
	};
	confettiConfig.isPaused = function() {
		return isPaused;
	};
	confettiConfig.remove = function() {
		stopConfetti();
		isPaused = false;
		particles = [];
	};
	confettiConfig.isRunning = function() {
		return isRunning;
	};
	
	return confettiConfig;
}
